/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import request from 'modules/common/utils/request';
import { API, CHAT_HISTORY_CHANNEL, CHAT_HISTORY_PAGE_SIZE } from './constants';
import { customChatActions } from './slice';

export function* sendMessageGenerator({ payload }) {

  try {
      const response = yield call(request, API.POST_CHAT, payload);
      yield put(customChatActions.sendMessageSucceeded(response));
    } catch (error) {
      yield put(customChatActions.sendMessageFailed(error?.message));
    }
}

export function* fetchChatHistoryGenerator({ type, payload }) {
  // a clear or reset only needs takeLatest to cancel the fetch that is in flight
  if (type !== customChatActions.fetchChatHistory.type) return;
  const before = payload?.before;
  try {
    const query = new URLSearchParams({
      limit: CHAT_HISTORY_PAGE_SIZE,
      channel: CHAT_HISTORY_CHANNEL,
      ...(before && { before }),
    }).toString();
    const response = yield call(request, API.GET_CHAT_HISTORY, { query });
    yield put(
      customChatActions.fetchChatHistorySucceeded({
        results: response?.results ?? [],
        nextCursor: response?.nextCursor,
        before,
      })
    );
  } catch (error) {
    yield put(customChatActions.fetchChatHistoryFailed(error?.message));
  }
}

export function* clearChatHistoryGenerator() {
  try {
    yield call(request, API.DELETE_CHAT_HISTORY);
    yield put(customChatActions.clearChatHistorySucceeded());
    yield call(toast.success, 'Chat history cleared');
  } catch (error) {
    yield put(customChatActions.clearChatHistoryFailed(error?.message));
    yield call(toast.error, 'Unable to clear chat history. Please try again.');
  }
}

export function* customChatSaga() {
  yield takeLatest(customChatActions.sendMessage.type, sendMessageGenerator);
  yield takeLatest(
    [
      customChatActions.fetchChatHistory.type,
      customChatActions.clearChatHistory.type,
      customChatActions.resetChatHistory.type,
    ],
    fetchChatHistoryGenerator
  );
  yield takeLatest(customChatActions.clearChatHistory.type, clearChatHistoryGenerator);
}

export default customChatSaga;
